import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import type { Division } from "../types";
import { persistCookie, getCookie } from "utils/cookieStorage";

interface TechFilterContextValue {
  selectedEmployeeIds: number[];
  toggleDivision: (division: Division) => void;
  toggleEmployee: (employeeId: number) => void;
  clearAll: () => void;
}

const TechFilterContext = createContext<TechFilterContextValue>({
  selectedEmployeeIds: [],
  toggleDivision: () => {},
  toggleEmployee: () => {},
  clearAll: () => {},
});

export const TechFilterProvider = ({ children }: { children: React.ReactNode }) => {
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>(() => {
    const saved = getCookie("calendarTechFilter");
    return Array.isArray(saved) ? saved : [];
  });

  useEffect(() => {
    persistCookie("calendarTechFilter", selectedEmployeeIds);
  }, [selectedEmployeeIds]);

  const toggleDivision = useCallback((division: Division) => {
    const allEmpIds = division.employees.map((e) => e.id);
    setSelectedEmployeeIds((prev) => {
      const allSelected = allEmpIds.every((id) => prev.includes(id));
      return allSelected
        ? prev.filter((id) => !allEmpIds.includes(id))
        : [...new Set([...prev, ...allEmpIds])];
    });
  }, []);

  const toggleEmployee = useCallback((employeeId: number) => {
    setSelectedEmployeeIds((prev) =>
      prev.includes(employeeId)
        ? prev.filter((id) => id !== employeeId)
        : [...prev, employeeId]
    );
  }, []);

  const clearAll = useCallback(() => setSelectedEmployeeIds([]), []);

  const value = useMemo(
    () => ({ selectedEmployeeIds, toggleDivision, toggleEmployee, clearAll }),
    [selectedEmployeeIds, toggleDivision, toggleEmployee, clearAll]
  );

  return (
    <TechFilterContext.Provider value={value}>
      {children}
    </TechFilterContext.Provider>
  );
};

export const useTechFilter = () => useContext(TechFilterContext);
